import React from 'react';
import { Table, Tabs, Alert, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { CrmFollowUpRow, CrmFollowUpsSummary } from '@/services/salesDashboard.service';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerSubQueuePanel } from '@/components/control-tower/ControlTowerSubQueuePanel';
import { EmptyQueueState } from './EmptyQueueState';

const allRows = (s: CrmFollowUpsSummary): CrmFollowUpRow[] => [
  ...s.stale_opportunities,
  ...s.overdue_activities,
  ...s.quotations_expiring,
  ...s.missing_customer_po,
];

export const CrmFollowUpZone: React.FC<{
  data?: CrmFollowUpsSummary;
  loading?: boolean;
  error?: string | null;
}> = ({ data, loading, error }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <ControlTowerSectionCard zone="crmFollowUps" sectionId="crm-followups" title="CRM follow-ups">
        <Spin />
      </ControlTowerSectionCard>
    );
  }
  if (error) {
    return (
      <ControlTowerSectionCard zone="crmFollowUps" sectionId="crm-followups" title="CRM follow-ups">
        <Alert type="warning" message={error} />
      </ControlTowerSectionCard>
    );
  }
  if (!data) return null;

  const total = allRows(data).length;
  const columns = [
    { title: 'Type', dataIndex: 'follow_up_type', key: 'follow_up_type', width: 140 },
    { title: 'Reference', dataIndex: 'title', key: 'title' },
    { title: 'Detail', dataIndex: 'subtitle', key: 'subtitle', ellipsis: true },
    { title: 'Date', dataIndex: 'due_or_reference_date', key: 'due_or_reference_date', width: 100 },
    {
      title: '',
      key: 'action',
      width: 70,
      render: (_: unknown, r: CrmFollowUpRow) => (
        <a onClick={() => navigate(r.link_path)}>Open</a>
      ),
    },
  ];

  const tableInPanel = (zone: Parameters<typeof ControlTowerSubQueuePanel>[0]['zone'], title: string, count: number, rows: CrmFollowUpRow[]) => (
    <ControlTowerSubQueuePanel zone={zone} title={title} count={count}>
      <Table rowKey="id" size="small" pagination={false} dataSource={rows} columns={columns} />
    </ControlTowerSubQueuePanel>
  );

  return (
    <ControlTowerSectionCard zone="crmFollowUps" sectionId="crm-followups" title={`CRM follow-ups (${total})`}>
      {total === 0 ? (
        <EmptyQueueState message="No CRM follow-ups due right now." />
      ) : (
        <Tabs
          size="small"
          items={[
            {
              key: 'all',
              label: `All (${total})`,
              children: tableInPanel('crmStale', 'All follow-ups', total, allRows(data)),
            },
            {
              key: 'stale',
              label: `Stale (${data.stale_opportunities.length})`,
              children: tableInPanel('crmStale', 'Stale opportunities', data.stale_opportunities.length, data.stale_opportunities),
            },
            {
              key: 'activities',
              label: `Activities (${data.overdue_activities.length})`,
              children: (
                <>
                  <a onClick={() => navigate('/growth/follow-ups')}>Open follow-up tracker</a>
                  {tableInPanel(
                    'crmActivities',
                    'Overdue follow-ups',
                    data.overdue_activities.length,
                    data.overdue_activities
                  )}
                </>
              ),
            },
            {
              key: 'quotes',
              label: `Quotes (${data.quotations_expiring.length})`,
              children: tableInPanel(
                'crmQuotations',
                'Quotations expiring',
                data.quotations_expiring.length,
                data.quotations_expiring
              ),
            },
            {
              key: 'po',
              label: `Missing PO (${data.missing_customer_po.length})`,
              children: tableInPanel(
                'crmMissingPo',
                'Missing customer PO',
                data.missing_customer_po.length,
                data.missing_customer_po
              ),
            },
          ]}
        />
      )}
    </ControlTowerSectionCard>
  );
};
